import React from 'react';
import { 
  FaUsers, FaCalendarAlt, FaProjectDiagram, FaMoneyBillWave,
  FaArrowUp, FaArrowDown, FaEye
} from 'react-icons/fa';
import '../css/standard-components.css';
import '../css/pages/Dashboard.css';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  change?: {
    value: string;
    isPositive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, change }) => (
  <div className="stat-card">
    <div className="stat-content">
      <div className="stat-info">
        <h3 className="stat-title">{title}</h3>
        <p className="stat-value">{value}</p>
        {change && (
          <div className={`stat-change ${change.isPositive ? 'positive' : 'negative'}`}>
            {change.isPositive ? <FaArrowUp /> : <FaArrowDown />}
            {change.value}
          </div>
        )}
      </div>
      <div className={`stat-icon ${color}`}>
        {icon}
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const stats = [
    {
      title: 'Total Projects',
      value: 24,
      icon: <FaProjectDiagram />,
      color: 'blue',
      change: { value: '+12%', isPositive: true }
    },
    {
      title: 'Active Bookings',
      value: 156,
      icon: <FaCalendarAlt />,
      color: 'green',
      change: { value: '+8%', isPositive: true }
    },
    {
      title: 'Total Users',
      value: 89,
      icon: <FaUsers />,
      color: 'purple',
      change: { value: '+15%', isPositive: true }
    },
    {
      title: 'Revenue',
      value: '₹12.5L',
      icon: <FaMoneyBillWave />,
      color: 'orange',
      change: { value: '+22%', isPositive: true }
    }
  ];

  const recentBookings = [
    { id: 1, customer: 'John Doe', project: 'Sky Heights', amount: '₹2.5L', status: 'Confirmed' },
    { id: 2, customer: 'Jane Smith', project: 'Green Valley', amount: '₹3.2L', status: 'Pending' },
    { id: 3, customer: 'Mike Johnson', project: 'Ocean View', amount: '₹1.8L', status: 'Confirmed' },
    { id: 4, customer: 'Sarah Wilson', project: 'City Center', amount: '₹4.1L', status: 'Under Review' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <FaProjectDiagram />
            Dashboard
          </h1>
          <p className="page-subtitle">Here's what's happening with your business today.</p>
        </div>
      </div>

      <div className="content-section">
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>
      </div>

      <div className="content-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-gray-900" style={{fontSize: '20px', fontWeight: '600', margin: 0}}>Recent Bookings</h2>
          <button className="btn btn-primary">
            <FaEye /> View All
          </button>
        </div>
        
        <table className="standard-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Customer</th>
              <th>Project</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {recentBookings.map((booking, index) => (
              <tr key={booking.id}>
                <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                  {recentBookings.length - index}
                </td>
                <td>{booking.customer}</td>
                <td>{booking.project}</td>
                <td className="amount">{booking.amount}</td>
                <td>
                  <span className={`status-badge status-${booking.status.toLowerCase().replace(' ', '-')}`}>
                    {booking.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="content-section">
        <h2 className="text-gray-900 mb-4" style={{fontSize: '20px', fontWeight: '600'}}>Quick Actions</h2>
        
        <div className="quick-actions">
          <button className="action-card">
            <FaCalendarAlt />
            <span>New Booking</span>
          </button>
          <button className="action-card">
            <FaProjectDiagram />
            <span>Add Project</span>
          </button>
          <button className="action-card">
            <FaUsers />
            <span>Manage Users</span>
          </button>
          <button className="action-card">
            <FaMoneyBillWave />
            <span>View Reports</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
